import React, { useState } from "react";
import { X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { createCategory } from "../../services/categoryService";
import ModalWrapper from "../Modal";
import { getErrorMessage } from "../../utils/getErrorMessage";

const AddCategoryModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const queryClient = useQueryClient();

  const { mutate: handleCreate, isPending } = useMutation({
    mutationFn: () => createCategory(formData),
    onSuccess: () => {
      toast.success("Category created successfully!");
      setFormData({ name: "", description: "" });
      onClose();
      if (onSuccess) onSuccess();
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create category."));
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    if (!formData.name.trim() || !formData.description.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    handleCreate();
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} hasPadding={false}>
      <div className="w-full max-w-[540px] sm:rounded-2xl rounded-none sm:mx-0 mx-0 sm:my-0 my-0 sm:p-0 p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#EEF1F3] pb-1 px-3 sm:px-4 pt-3 sm:pt-4">
          <h2 className="text-xl font-medium text-[#0C0C0E]">Add Category</h2>
          <button
            onClick={onClose}
            className="text-[#4F535D] hover:text-[#6B7143] transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        {/* Form Body */}
        <div className="space-y-6 sm:px-4 px-3 sm:py-6 py-5">
          {/* Category Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#383B41]">
              Category Name <span>*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Men Fashion"
              className="w-full rounded-xl border border-[#D9DBDE] px-4 py-3.5 placeholder:text-gray-300 outline-none focus:border-gray-400 transition-all shadow-sm"
            />
          </div>
          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#383B41]">
              Description <span>*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="e.g., Men fashion category"
              rows="3"
              className="w-full rounded-xl border border-[#D9DBDE] px-4 py-3.5 placeholder:text-gray-300 outline-none focus:border-gray-400 transition-all shadow-sm resize-none"
            />
          </div>
        </div>
        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:px-4 px-3 sm:py-3 py-3 border-t border-[#EEF1F3]">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl bg-gradient-to-r from-white to-[#F3F3F3] border border-[#D9DBDE] py-3.5 text-base font-medium text-[#4F535D] hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending}
            className="flex-1 rounded-xl bg-[#D9C9B8] py-3.5 text-base font-medium text-gray-800 hover:bg-[#C8B8A7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default AddCategoryModal;
